import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { User } from '../dataProvider/types';

/**
 * Log a new erasure request (D-erasure). The guarded-create-append idiom (the consent-withdrawal
 * precedent): the create is GUARDED on `erasure:request` (compliance + super_admin) in the component,
 * then a plain `dataProvider.create` lands the row, audited for free. A logged request enters at
 * `requested` and the grace window starts; it does NOT erase anything (execute is the separate,
 * super_admin gated, grace-elapsed act).
 */
export const RequestErasureAction = ({ now }: { now: number }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [memberId, setMemberId] = useState('');

  if (
    !hasAccess(
      { role, accessibleRegions: [] },
      { capability: 'erasure:request' },
    )
  ) {
    return null;
  }

  const memberValid = /^\d+$/.test(memberId.trim());

  const onConfirm = async () => {
    setLoading(true);
    try {
      const id = Number(memberId.trim());
      const user = (await dataProvider.getOne('users', { id })).data as User;
      await dataProvider.create('erasureRequests', {
        data: {
          subjectUserId: id,
          market: user.market,
          status: 'requested',
          requestedAt: new Date(now).toISOString(),
          executedAt: null,
          cancelledAt: null,
          rejectedAt: null,
          rejectionReason: null,
        },
      });
      notify('Erasure request logged', { type: 'success' });
      setMemberId('');
      setOpen(false);
      setLoading(false);
      refresh();
    } catch {
      notify('Could not log the request (check the member id)', {
        type: 'error',
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        data-testid="erasure-request-new"
      >
        Log erasure request
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Log an erasure request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Log a right-to-be-forgotten request for a member. The grace window
            starts now; nothing is erased until the window elapses and the
            request is executed.
          </DialogContentText>
          <TextField
            label="Member id"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            fullWidth
            error={memberId.length > 0 && !memberValid}
            helperText="The member whose data the request is to erase"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!memberValid || loading}
            onClick={onConfirm}
          >
            Log request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
